import { Location } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { LoadingService } from "../../../../core/loader/loading.service";
import { DataTableComponent } from "../../../../shared/components/data-table/data-table.component";
import {
  DataTable,
  DataTableParamsWhere,
  GetDataEvent,
} from "../../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../../shared/components/delete-modal/delete-modal.component";
import { DynamicFormComponent } from "../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { GenericModalComponent } from "../../../../shared/components/generic-modal/generic-modal.component";
import { GenericModalData } from "../../../../shared/components/generic-modal/generic-modal.type";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import { UserInfo } from "../../../../shared/types/user-info";
import { isRequiredField } from "../../../../shared/utils/helpers/common.helper";
import { PartnerTableService } from "../../partners/partner-table.service";
import { PartnerService } from "../../partners/partner.service";
import { Partner } from "../../partners/partner.type";
import {
  CreateMaintenanceProviderService,
  ICreateMaintenanceForm,
} from "../create-maintenance-provider/create-maintenance-provider.service";
import { MaintenanceProviderService } from "../maintenance-provider.service";
import { MaintenanceProvider } from "../maintenance-provider.types";

@Component({
  selector: "app-manage-maintenance-provider",
  templateUrl: "./manage-maintenance-provider.component.html",
  styleUrls: ["./manage-maintenance-provider.component.scss"],
  providers: [CreateMaintenanceProviderService],
})
export class ManageMaintenanceProviderComponent implements OnInit {
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;

  currentUser: UserInfo;
  loading = false;
  maintenanceProvider: MaintenanceProvider;
  isEdit: boolean;
  questions: QuestionBase<string | boolean>[];
  partnersTableConfig: DataTable<Partner>;
  public isRequiredField = isRequiredField;

  constructor(
    public dialog: MatDialog,
    public partnersTableService: PartnerTableService,
    public location: Location,
    private loader: LoadingService,
    private route: ActivatedRoute,
    private router: Router,
    private maintenanceProviderService: MaintenanceProviderService,
    private questionService: CreateMaintenanceProviderService,
    private notificationService: NotificationService,
    private authenticationService: AuthenticationService,
    private partnerService: PartnerService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
    this.loader.show();
  }

  ngOnInit(): void {
    const maintenanceProviderId: string = this.route.snapshot.paramMap.get("id");
    this.getMaintenanceProvider(maintenanceProviderId);
    this.setTabs();
  }

  goBack(): void {
    this.location.back();
  }

  getMaintenanceProvider(id: string): Subscription {
    return this.maintenanceProviderService.getMaintenanceProvider(id, { relations: { partners: true } }).subscribe(
      (maintenanceProvider: MaintenanceProvider) => {
        this.maintenanceProvider = maintenanceProvider;
        this.buildForm(maintenanceProvider);
      },
      (error) => {
        this.notificationService.error(error || "Couldn't find Maintenance Provider");
        this.router.navigate(["/maintenance-providers"]);
      },
    );
  }

  buildForm(provider: MaintenanceProvider): void {
    this.questionService
      .generateQuestions({
        nameValue: provider.name,
        emailValue: provider.email,
        phoneValue: provider.phone,
        isChannelWorksValue: provider.isChannelWorks,
        enabledValue: provider.enabled,
        isDefault: provider.isDefault,
      })
      .subscribe((qs) => {
        this.questions = qs;
        this.loader.hide();
      });
  }

  toggleManageConfig(): void {
    this.isEdit = !this.isEdit;
  }

  onSubmit(values: ICreateMaintenanceForm): void {
    this.loader.show();
    this.maintenanceProviderService
      .updateMaintenanceProvider(this.maintenanceProvider.id, { ...values })
      .pipe(finalize(() => this.dynamicForm.enable()))
      .subscribe(
        (maintenanceProvider): void => {
          this.notificationService.success("Maintenance provider successfully edited");
          this.maintenanceProvider = maintenanceProvider;
          this.toggleManageConfig();
          this.loader.hide();
        },
        (error) => {
          this.loader.hide();
          this.notificationService.error(error ?? "Could not update maintenance provider");
        },
      );
  }

  deleteConfigModal(): void {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "maintenance provider",
        data: this.maintenanceProvider,
        overwrittenUrl: `${this.maintenanceProviderService.basePath}/${this.maintenanceProvider.id}`,
        warningEntity: "maintenanceProvider",
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.router.navigate(["/maintenance-providers"]);
      }
    });
  }

  getPartners = (event: GetDataEvent<Partner>): void => {
    const maintenanceProviderId: string = this.route.snapshot.paramMap.get("id");
    const { onError, onResult, queryParams } = event;
    this.partnerService
      .getPartners({
        ...queryParams,
        relations: { maintenanceProviders: true },
        where: {
          ...queryParams?.where,
          maintenanceProviders: { id: maintenanceProviderId },
        },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  // #region Partner Tab
  private setPartnerTab(): void {
    this.partnersTableConfig = this.partnersTableService.getPartnersTableConfig({
      actionColumn: { delete: { hidden: true } },
    });
    this.partnersTableConfig = {
      ...this.partnersTableConfig,
      actionColumn: [
        ...this.partnersTableConfig.actionColumn,
        {
          icon: "link_off",
          text: "Unlink",
          function: this.unlinkPartnerModal,
          hidden: !this.currentUser.isGlobalAdmin(),
        },
      ],
      toolBar: {
        ...this.partnersTableConfig.toolBar,
        addItem: {
          function: this.showLinkPartnersToUserModal,
          hidden: !this.currentUser.isGlobalAdmin(),
        },
      },
    };
  }
  // #endregion

  private unlinkPartnerModal = (partner: Partner, table: DataTableComponent<Partner>): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "Partner",
        data: partner,
        unlink: true,
        fullUrl: `${this.maintenanceProviderService.basePath}/${this.maintenanceProvider.id}/partners`,
        body: { partners: [partner.id] },
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };

  private showLinkPartnersToUserModal = async (table: DataTableComponent<Partner>): Promise<void> => {
    // Assign partners to mp (used in the popup table)
    const action = (partnersTable: DataTableComponent<Partner>) => {
      if (partnersTable.config.selection.selectedRows.length) {
        this.loader.show();
        const body = { partners: partnersTable.config.selection.selectedRows.map((partner) => partner.id) };
        this.maintenanceProviderService
          .linkPartners(this.maintenanceProvider.id, body)
          .subscribe(
            () => {
              this.notificationService.success("Partners linked");
              table.search();
            },
            (error) => this.notificationService.error(`Couldn't link partners to user: ${error}`),
          )
          .add(() => this.loader.hide());
      }
    };
    this.maintenanceProvider = await this.maintenanceProviderService
      .getMaintenanceProvider(this.maintenanceProvider.id, {
        relations: { partners: true },
      })
      .toPromise();

    this.dialog.open<GenericModalComponent, GenericModalData<{ config: DataTable<Partner> }>>(GenericModalComponent, {
      width: "1400px",
      panelClass: "custom-dialog-container",
      data: {
        title: `Link partners to ${this.maintenanceProvider.name}`,
        buttons: { hide: false, submit: { action } },
        component: {
          clazz: DataTableComponent,
          inputs: {
            config: this.partnersTableService.getPartnersTableConfig({
              actionColumn: { hidden: true },
              inlineEdit: { disabled: () => true },
              syncWithQueryParams: false,
              selection: { hidden: false },
            }),
          },
          events: [
            {
              eventName: "getData",
              handler: (_dynamicForm: any, event: GetDataEvent<Partner>) => {
                return this.partnersTableService.getPartnersPost({
                  ...event,
                  queryParams: {
                    ...event.queryParams,
                    where: {
                      ...event.queryParams.where,
                      id: { $nin: this.maintenanceProvider.partners.map((p) => p.id) } as any,
                    } as DataTableParamsWhere<Partner>,
                  },
                });
              },
            },
          ],
        },
      },
    });
  };

  private setTabs(): void {
    this.setPartnerTab();
  }
}
